/**
 * The SAL-00 number chip — the document's own number, drawn beside the fixed
 * page title on each of the five screens built from the shared layout
 * (§6.6): sales invoice, sales return, purchase order, purchase invoice,
 * purchase return.
 *
 * BR-SAL-00-02 — "The number label must name the document: 'Invoice No.' on
 * a sales invoice, 'Return No.' on a sales return, 'Order No.' on a purchase
 * order, 'Bill No.' on a purchase invoice, 'Debit note No.' on a purchase
 * return. Each screen's label is fixed — it never switches, because the
 * screen never changes what it is."
 *
 * ── THE TWO HALVES, AND WHERE EACH IS ENFORCED ────────────────────────────
 *
 * The NEVER-SWITCHES half is owner ruling R3 by construction: each of the
 * five is its own route component with its own hard-coded title, route and
 * number series, so no state exists anywhere from which a label could
 * switch. The rule was written against the prototype's shared screen, whose
 * type dropdown left `Invoice No.` over a purchase order's number (§6.7);
 * that screen does not exist.
 *
 * The NAMING half is enforced here: `label` is a REQUIRED prop, rendered as
 * the chip's own visually-hidden text ahead of the number, so the number is
 * never announced bare — a screen reader hears "Invoice No. INV-2042", and
 * each screen's label is the fixed one for what that screen is. Visually
 * nothing changes: the chip already sits beside a heading that names the
 * document, and whether the number should ALSO carry a visible label is a
 * screen-design question left with the owner (recorded at the SAL-00 queue
 * paragraph in `invoice-editor-page.tsx`), which an `sr-only` label neither
 * decides nor forecloses.
 *
 * ── THE LABEL STRINGS, AND TWO RECORDED DEVIATIONS ────────────────────────
 *
 * Each screen passes its own list column's key — `sales.colNo`,
 * `returns.colNo`, `po.colNo`, `bills.colNo`, `preturn.colNo` — because the
 * spec fixes the SAME string on both surfaces: the list's number column and
 * the open document's number label are both required to name the document,
 * so sharing the key is the coupling being true, not a shortcut. Two of the
 * five differ from the rule's literal strings, deliberately:
 *
 *   - the sales return's label reads "Credit Note No." where the rule writes
 *     "Return No." — the spec's own synonym for this exact document (§6.3
 *     "Sales Return (credit note)", the `CN-` series), and the name the rest
 *     of the built screen already uses in every sentence;
 *   - the purchase return's reads "Debit Note No." against the rule's
 *     "Debit note No." — casing only.
 *
 * Both name the document, fixed per screen, which is the obligation.
 *
 * Guard: `src/test/sal00-number-label.test.tsx` — the five screens by name,
 * each passing its own key, and the rendered chip announcing the label.
 */
export function DocumentNumberChip({
  label,
  code,
  fallback,
}: {
  /** The fixed, translated number label for what this screen is. */
  label: string;
  /** The document's number, or null before the first save mints one. */
  code: string | null;
  /** What the chip shows while there is no number yet (BR-SAL-00-06). */
  fallback: string;
}) {
  return (
    <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
      {code === null ? (
        fallback
      ) : (
        <>
          <span className="sr-only">{label} </span>
          <bdi dir="ltr">{code}</bdi>
        </>
      )}
    </span>
  );
}
